import { useCallback, useRef, useState } from 'react';
import { videoService, VideoMetadata, VideoQuality } from '../services/api';
import type { BatchQueueItem, ZipProgress } from '../services/mediaContracts';
import {
  getBestMp4Url,
  getImageExtension,
  parseXPostUrls,
  sanitizeForFilename,
  toOriginalImageUrl,
} from '../services/mediaUtils';
import { downloadItemsAsZip } from '../services/zipDownload';

const getSelectedVideoUrl = (
  video: VideoMetadata['videos'][number],
  quality: VideoQuality
): string => getBestMp4Url(video.variants, video.url, quality);

export interface BatchDownloadResult {
  mode: 'zip';
  saved: number;
  total: number;
  zipName?: string;
  message: string;
}

export function useBatchQueue() {
  const [items, setItems] = useState<BatchQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState<ZipProgress | null>(null);
  const runTokenRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const addUrls = useCallback((raw: string | string[]) => {
    const list = Array.isArray(raw) ? raw : parseXPostUrls(raw);
    const prev = itemsRef.current;
    const existingIds = new Set(
      prev
        .map((i) => i.url.match(/status\/(\d+)/)?.[1])
        .filter(Boolean) as string[]
    );
    const existingUrls = new Set(prev.map((i) => i.url));
    const next = [...prev];

    for (const url of list) {
      const normalized = url.trim();
      if (!normalized) continue;
      const id = normalized.match(/status\/(\d+)/)?.[1];
      if (existingUrls.has(normalized)) continue;
      if (id && existingIds.has(id)) continue;
      existingUrls.add(normalized);
      if (id) existingIds.add(id);
      next.push({ url: normalized, status: 'pending' });
    }

    // Sync ref immediately so enqueueAndRun → runQueue sees new pending items
    itemsRef.current = next;
    setItems(next);
  }, []);

  const runQueue = useCallback(async () => {
    const token = ++runTokenRef.current;
    setIsRunning(true);

    try {
      while (token === runTokenRef.current) {
        const pending = itemsRef.current.find((i) => i.status === 'pending');
        if (!pending) break;

        const url = pending.url;
        setItems((prev) =>
          prev.map((i) =>
            i.url === url
              ? { ...i, status: 'fetching' as const, error: undefined }
              : i
          )
        );
        // Keep ref in sync for next iteration before await
        itemsRef.current = itemsRef.current.map((i) =>
          i.url === url
            ? { ...i, status: 'fetching' as const, error: undefined }
            : i
        );

        try {
          const metadata = await videoService.getVideoInfo(url);
          if (token !== runTokenRef.current) return;
          setItems((prev) => {
            const updated = prev.map((i) =>
              i.url === url
                ? { ...i, status: 'ready' as const, metadata, error: undefined }
                : i
            );
            itemsRef.current = updated;
            return updated;
          });
        } catch (err) {
          if (token !== runTokenRef.current) return;
          const message =
            err instanceof Error ? err.message : 'Failed to fetch post';
          setItems((prev) => {
            const updated = prev.map((i) =>
              i.url === url
                ? { ...i, status: 'error' as const, error: message }
                : i
            );
            itemsRef.current = updated;
            return updated;
          });
        }
      }
    } finally {
      if (token === runTokenRef.current) {
        setIsRunning(false);
      }
    }
  }, []);

  const collectMediaItems = useCallback(
    (readyItems: BatchQueueItem[], quality: VideoQuality) => {
      const media: Array<{ url: string; filename: string }> = [];

      readyItems.forEach((item, postIndex) => {
        const meta = item.metadata;
        if (!meta) return;
        const prefix = sanitizeForFilename(
          `${meta.author || 'x'}_${meta.tweetId || postIndex + 1}`
        );

        meta.videos.forEach((video, vi) => {
          const mediaUrl = getSelectedVideoUrl(video, quality);
          if (mediaUrl) {
            media.push({
              url: mediaUrl,
              filename: `${prefix}_video_${vi + 1}.mp4`,
            });
          }
        });

        (meta.images || []).forEach((image, ii) => {
          const mediaUrl = toOriginalImageUrl(image.url);
          if (mediaUrl) {
            media.push({
              url: mediaUrl,
              filename: `${prefix}_image_${ii + 1}.${getImageExtension(mediaUrl)}`,
            });
          }
        });
      });

      return media;
    },
    []
  );

  const downloadReadyAsZip = useCallback(
    async (quality: VideoQuality = 'high'): Promise<BatchDownloadResult> => {
      const ready = itemsRef.current.filter(
        (i) => i.status === 'ready' && i.metadata
      );
      if (ready.length === 0) {
        throw new Error('No ready items to download');
      }

      const media = collectMediaItems(ready, quality);
      if (media.length === 0) {
        throw new Error('No media found in ready items');
      }

      setIsZipping(true);
      setZipProgress({ current: 0, total: media.length });

      try {
        const zipName = `x_batch_${ready.length}_posts.zip`;
        const result = await downloadItemsAsZip(media, zipName, setZipProgress);

        setItems((prev) => {
          const updated = prev.map((i) =>
            i.status === 'ready' ? { ...i, status: 'done' as const } : i
          );
          itemsRef.current = updated;
          return updated;
        });

        return {
          mode: 'zip',
          saved: result.saved,
          total: result.total,
          zipName: result.zipName,
          message:
            result.saved < result.total
              ? `Saved ${result.saved} of ${result.total} files as ${result.zipName}`
              : `Saved ${result.saved} files as ${result.zipName}`,
        };
      } finally {
        setIsZipping(false);
        setZipProgress(null);
      }
    },
    [collectMediaItems]
  );

  const clearQueue = useCallback(() => {
    runTokenRef.current += 1;
    setItems([]);
    itemsRef.current = [];
    setIsRunning(false);
    setIsZipping(false);
    setZipProgress(null);
  }, []);

  const removeItem = useCallback((url: string) => {
    setItems((prev) => {
      const updated = prev.filter((i) => i.url !== url);
      itemsRef.current = updated;
      return updated;
    });
  }, []);

  const retryFailed = useCallback(async () => {
    setItems((prev) => {
      const updated = prev.map((i) =>
        i.status === 'error'
          ? { ...i, status: 'pending' as const, error: undefined }
          : i
      );
      itemsRef.current = updated;
      return updated;
    });
    await Promise.resolve();
    await runQueue();
  }, [runQueue]);

  /** Add URLs and immediately start sequential fetch. */
  const enqueueAndRun = useCallback(
    async (raw: string | string[]) => {
      addUrls(raw);
      await Promise.resolve();
      await runQueue();
    },
    [addUrls, runQueue]
  );

  const readyCount = items.filter((i) => i.status === 'ready').length;
  const errorCount = items.filter((i) => i.status === 'error').length;
  const pendingCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'fetching'
  ).length;

  return {
    items,
    isRunning,
    isZipping,
    zipProgress,
    readyCount,
    errorCount,
    pendingCount,
    addUrls,
    enqueueAndRun,
    runQueue,
    downloadReadyAsZip,
    clearQueue,
    removeItem,
    retryFailed,
  };
}
